import * as React from 'react';

import type { KbFilters } from '@/api/kb-client';
import { useKbAdminClient } from '@/api/kb-client-context';
import { useKbThreadsQuery } from '@/api/use-kb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KbTagPanel } from '@/routes/admin/communication/kb-tag-panel';
import { KbThreadDetail } from '@/routes/communication/kb-thread-detail';
import { KbThreadList } from '@/routes/communication/kb-thread-list';

// =============================================================================
// kb-admin-screen.tsx — KB ADMIN « Base de connaissance » (R15-COM-04, §2.7.4).
// =============================================================================
// KB Admin TRANSVERSE (tous SIRET) : recherche full-text (`q`, surlignage), filtres (fournisseur SIRET,
// campagne, période d'archivage), liste des fils ARCHIVÉS, vue détail LECTURE SEULE, panneau TAGS
// (taxonomie), et EXPORT (JSON/CSV — ADMIN ONLY, téléchargement via `<a href>`, Content-Disposition
// serveur). Composant PRÉSENTATIONNEL : l'état de vue (filtres + fil ouvert) est porté par l'URL via
// l'adaptateur routeur (RULE_REF_006). WCAG 2.1 AA : régions titrées, <label> associés, focus visible.

export interface KbAdminScreenProps {
  readonly filters: KbFilters;
  readonly onFiltersChange: (filters: KbFilters) => void;
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (threadId: string | null) => void;
}

export function KbAdminScreen({
  filters,
  onFiltersChange,
  selectedThreadId,
  onSelectThread,
}: KbAdminScreenProps): React.JSX.Element {
  const threadsQuery = useKbThreadsQuery('admin', filters);
  const client = useKbAdminClient();

  // Brouillons locaux des champs de recherche/filtres (appliqués à la soumission du formulaire).
  const [qDraft, setQDraft] = React.useState(filters.q ?? '');
  const [siretDraft, setSiretDraft] = React.useState(filters.supplierSiret ?? '');
  const [campaignDraft, setCampaignDraft] = React.useState(filters.campaignId ?? '');
  const [fromDraft, setFromDraft] = React.useState(filters.archivedFrom ?? '');
  const [toDraft, setToDraft] = React.useState(filters.archivedTo ?? '');

  React.useEffect(() => {
    setQDraft(filters.q ?? '');
    setSiretDraft(filters.supplierSiret ?? '');
    setCampaignDraft(filters.campaignId ?? '');
    setFromDraft(filters.archivedFrom ?? '');
    setToDraft(filters.archivedTo ?? '');
  }, [
    filters.q,
    filters.supplierSiret,
    filters.campaignId,
    filters.archivedFrom,
    filters.archivedTo,
  ]);

  // Reconstruit un KbFilters SANS clé `undefined` (exactOptionalPropertyTypes) : clé absente = pas de filtre.
  const buildFromDrafts = (): KbFilters => {
    const q = qDraft.trim();
    const siret = siretDraft.trim();
    const campaign = campaignDraft.trim();
    const from = fromDraft.trim();
    const to = toDraft.trim();
    return {
      ...(q.length > 0 ? { q } : {}),
      ...(siret.length > 0 ? { supplierSiret: siret } : {}),
      ...(campaign.length > 0 ? { campaignId: campaign } : {}),
      ...(from.length > 0 ? { archivedFrom: from } : {}),
      ...(to.length > 0 ? { archivedTo: to } : {}),
    };
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onFiltersChange(buildFromDrafts());
  };

  const jsonUrl = client.buildExportUrl('json', filters);
  const csvUrl = client.buildExportUrl('csv', filters);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl font-semibold tracking-tight">
          Base de connaissance
        </h1>
        <p className="text-sm text-muted-foreground">
          Archives consultables des conversations clôturées, tous fournisseurs. Recherche plein
          texte, tags et export.
        </p>
      </header>

      <Card className="p-4">
        <form
          onSubmit={onSubmit}
          role="search"
          className="flex flex-wrap items-end gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-search-q">Recherche plein texte</Label>
            <Input
              id="kb-search-q"
              value={qDraft}
              onChange={(event) => setQDraft(event.target.value)}
              placeholder="Mots-clés du contenu"
              className="w-56"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-search-siret">Fournisseur (SIRET)</Label>
            <Input
              id="kb-search-siret"
              value={siretDraft}
              onChange={(event) => setSiretDraft(event.target.value)}
              placeholder="14 chiffres"
              inputMode="numeric"
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-search-campaign">Campagne</Label>
            <Input
              id="kb-search-campaign"
              value={campaignDraft}
              onChange={(event) => setCampaignDraft(event.target.value)}
              placeholder="Identifiant de campagne"
              className="w-52"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-search-from">Archivé depuis</Label>
            <Input
              id="kb-search-from"
              type="date"
              value={fromDraft}
              onChange={(event) => setFromDraft(event.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-search-to">Archivé jusqu'au</Label>
            <Input
              id="kb-search-to"
              type="date"
              value={toDraft}
              onChange={(event) => setToDraft(event.target.value)}
              className="w-40"
            />
          </div>
          <Button type="submit" variant="secondary">
            Rechercher
          </Button>
        </form>

        {/* EXPORT (ADMIN ONLY) : liens de téléchargement portant les filtres COURANTS (URL appliquée). */}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <span className="text-sm font-medium text-foreground">
            Exporter le périmètre filtré
          </span>
          <Button asChild variant="outline">
            <a href={jsonUrl} download>
              Export JSON
            </a>
          </Button>
          <Button asChild variant="outline">
            <a href={csvUrl} download>
              Export CSV
            </a>
          </Button>
        </div>
      </Card>

      <div className="grid min-h-[32rem] grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <Card
          aria-label="Liste des conversations archivées"
          className="min-h-0 overflow-y-auto"
        >
          <KbThreadList
            profile="admin"
            threads={threadsQuery.data?.threads ?? []}
            selectedThreadId={selectedThreadId}
            onSelectThread={onSelectThread}
            isLoading={threadsQuery.isLoading}
          />
        </Card>

        <Card className="flex min-h-0 flex-col">
          {selectedThreadId !== null ? (
            <>
              <KbThreadDetail profile="admin" threadId={selectedThreadId} />
              <KbTagPanel threadId={selectedThreadId} />
            </>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              Sélectionnez une conversation archivée dans la liste pour l'afficher.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
